/**
 * AssessmentDesignPolicies.jsx — Phase 2A-4
 * State machine: DRAFT → PUBLISHED
 * Designer roles only for create/publish. All allowed roles can view.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Plus, RefreshCw, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDesignShell, useIsDesigner } from "../components/assessment-design/AssessmentDesignShell";
import { LifecycleBadge } from "../components/assessment-design/LifecycleBadge";
import { PublishBlockers } from "../components/assessment-design/PublishBlockers";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { "Authorization": `Bearer ${localStorage.getItem("academiaos_token")}`, "Content-Type": "application/json" }; }
function apiError(e, fb = "Error") {
  const d = e?.detail || e?.message || fb;
  if (Array.isArray(d)) return d.map(x => x.msg || JSON.stringify(x)).join("; ");
  return String(d);
}

const EMPTY_FORM = { policy_code: "", title: "", description: "", requires_policy_approval: false };

export default function AssessmentDesignPolicies() {
  const isDesigner = useIsDesigner();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(null); // policy id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/assessment/policies`, { headers: authH() });
      if (!r.ok) throw await r.json().catch(() => ({}));
      setPolicies(await r.json());
    } catch (e) {
      toast.error("Failed to load policies: " + apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await fetch(`${API}/assessment/policies`, {
        method: "POST", headers: authH(),
        body: JSON.stringify({
          policy_code: form.policy_code,
          title: form.title,
          description: form.description || undefined,
          requires_policy_approval: form.requires_policy_approval,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Policy "${data.title}" created`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error("Create failed: " + apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (pol) => {
    setPublishing(pol.id);
    try {
      const idemKey = `pub-pol-${pol.id}`;
      const r = await fetch(`${API}/assessment/policies/${pol.id}/publish`, {
        method: "POST",
        headers: { ...authH(), "Idempotency-Key": idemKey },
      });
      const data = await r.json();
      if (!r.ok) throw data;
      toast.success(`Policy "${data.title}" published`);
      load();
    } catch (e) {
      const detail = e?.detail;
      if (detail?.error === "stale_version") {
        toast.error("Stale version — please refresh and try again.");
      } else if (detail?.error === "permission_denied") {
        toast.error(`Permission denied: requires ${detail.required_roles?.join(", ")}`);
      } else {
        toast.error("Publish failed: " + apiError(e));
      }
    } finally {
      setPublishing(null);
    }
  };

  return (
    <AssessmentDesignShell title="Assessment Policies" breadcrumb="Policies">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-slate-400">
            Policies define institutional rules for assessment governance.
            State machine: <span className="font-mono text-slate-300">DRAFT → PUBLISHED</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="policies-refresh-btn"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isDesigner && (
            <button
              onClick={() => setShowCreate(true)}
              data-testid="policy-create-button"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white transition-colors"
            >
              <Plus className="w-4 h-4" /> New Policy
            </button>
          )}
        </div>
      </div>

      {/* ── Policy list ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="h-16 rounded-lg bg-slate-800/40 animate-pulse" />
          ))}
        </div>
      ) : policies.length === 0 ? (
        <div data-testid="policies-empty" className="text-center py-16 text-slate-500">
          <p className="text-sm">No assessment policies yet.</p>
          {isDesigner && (
            <button onClick={() => setShowCreate(true)}
              className="mt-3 text-sky-400 text-sm hover:text-sky-300">
              Create the first policy →
            </button>
          )}
        </div>
      ) : (
        <div data-testid="policies-list" className="space-y-2">
          {policies.map(pol => (
            <div
              key={pol.id}
              data-testid={`policy-row-${pol.id}`}
              className="flex items-center gap-4 p-4 rounded-xl border border-slate-800 bg-slate-900/50 hover:border-slate-700 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-slate-100 truncate">{pol.title}</span>
                  <LifecycleBadge status={pol.status} />
                  {pol.requires_policy_approval && (
                    <span className="text-xs px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">Approval required</span>
                  )}
                </div>
                <span className="text-xs text-slate-500 font-mono">{pol.policy_code}</span>
                {pol.description && (
                  <p className="text-xs text-slate-400 mt-1 truncate">{pol.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isDesigner && pol.status === "DRAFT" && (
                  <button
                    onClick={() => handlePublish(pol)}
                    disabled={publishing === pol.id}
                    data-testid={`policy-publish-btn-${pol.id}`}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-300 border border-emerald-700/50 transition-colors disabled:opacity-50"
                  >
                    {publishing === pol.id ? "Publishing…" : "Publish"}
                  </button>
                )}
                {pol.status === "PUBLISHED" && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <ChevronRight className="w-3 h-3" /> Immutable
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create modal ─────────────────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div data-testid="policy-create-modal" className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-base font-semibold text-slate-100">Create Assessment Policy</h2>
              <button onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Policy Code *</label>
                <input
                  data-testid="policy-code-input"
                  value={form.policy_code}
                  onChange={e => setForm(f => ({ ...f, policy_code: e.target.value }))}
                  required
                  placeholder="e.g. AP-2026-01"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Title *</label>
                <input
                  data-testid="policy-title-input"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  required
                  placeholder="Assessment Policy Title"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Description</label>
                <textarea
                  data-testid="policy-description-input"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  placeholder="Optional policy description"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 placeholder:text-slate-500 resize-none"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="requires_approval"
                  data-testid="policy-requires-approval-checkbox"
                  checked={form.requires_policy_approval}
                  onChange={e => setForm(f => ({ ...f, requires_policy_approval: e.target.checked }))}
                  className="w-4 h-4 rounded bg-slate-800 border-slate-600 text-sky-500"
                />
                <label htmlFor="requires_approval" className="text-sm text-slate-300">
                  Requires policy approval
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
                  className="px-4 py-2 rounded-lg text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={saving} data-testid="policy-create-submit-btn"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-50 transition-colors">
                  {saving ? "Creating…" : "Create Policy"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AssessmentDesignShell>
  );
}
